import { msalInstance } from "./msalInstance";
import { InteractionRequiredAuthError, AuthenticationResult } from "@azure/msal-browser";


export interface LoginOptions {
  scopes: string[];
  prompt?: "login" | "none" | "select_account" | "consent";
  loginHint?: string;
  domainHint?: string;
  idp?: string;
  forceRefresh?: boolean;
  interactionMode?: "popup" | "redirect";
}
export async function trySalesforceSilentSSO(options: {
  scopes: string[];
  loginHint?: string;
  domainHint?: string;
  idp?: string;
}): Promise<AuthenticationResult | null> {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts.find(acc => acc.username === options.loginHint) || accounts[0];
  if (!account) return null;
  
  try {
    // For external IdPs, avoid silent acquisition which can cause iframe issues
    if (options.domainHint && options.domainHint.includes('salesforce.com')) {
      return null;
    }
    
    const extraQueryParameters: Record<string, string> = {};
    if (options.domainHint) {
      extraQueryParameters.domain_hint = options.domainHint;
    }
    if (options.idp) {
      extraQueryParameters.idp = options.idp;
    }
    
    return await msalInstance.acquireTokenSilent({
      scopes: options.scopes,
      account,
      ...(options.loginHint ? { loginHint: options.loginHint } : {}),
      ...(Object.keys(extraQueryParameters).length > 0 ? { extraQueryParameters } : {}),
      prompt: "none"
    });
  } catch (error: any) {
    return null;
  }
}
export async function acquireTokenWithFallback(options: LoginOptions): Promise<AuthenticationResult> {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];

  try {
    if (!account) throw { errorCode: "no_account_error" };
    
    // For external IdPs (Google), avoid silent acquisition which uses iframes
    const isExternalIdP = options.domainHint && 
      (options.domainHint.includes('google.com') || options.domainHint.includes('salesforce.com'));
    
    if (isExternalIdP && options.prompt === "none") {
      throw { errorCode: "interaction_required" };
    }
    
    const extraQueryParameters: Record<string, string> = {};
    if (options.domainHint) {
      extraQueryParameters.domain_hint = options.domainHint;
    }
    if (options.idp) {
      extraQueryParameters.idp = options.idp;
    }
    
    return await msalInstance.acquireTokenSilent({
      scopes: options.scopes,
      account,
      forceRefresh: options.forceRefresh,
      ...(options.loginHint ? { loginHint: options.loginHint } : {}),
      ...(Object.keys(extraQueryParameters).length > 0 ? { extraQueryParameters } : {}),
      prompt: "none"
    });
  } catch (error: any) {
    const isInteractionRequired =
      error instanceof InteractionRequiredAuthError ||
      error.errorCode === "no_account_error" ||
      error.errorCode === "login_required" ||
      error.errorCode === "interaction_required" ||
      error.errorCode === "monitor_window_timeout" ||
      error.message?.includes("iframe") ||
      error.message?.includes("navigation");

    if (isInteractionRequired) {
      const extraQueryParameters: Record<string, string> = {};
      if (options.domainHint) {
        extraQueryParameters.domain_hint = options.domainHint;
      }
      if (options.idp) {
        extraQueryParameters.idp = options.idp;
      }
      
      const loginRequest = {
        scopes: options.scopes,
        prompt: options.prompt === "none" ? "select_account" : (options.prompt || "login"),
        ...(options.loginHint ? { loginHint: options.loginHint } : {}),
        ...(Object.keys(extraQueryParameters).length > 0 ? { extraQueryParameters } : {}),
      };
      
      console.log('🔐 MSAL Login Mode:', options.interactionMode || 'popup');
      console.log('🔐 Query Parameters:', extraQueryParameters);
      console.log('🔐 Full Login Options:', loginRequest);
      console.log('🔐 MSAL Instance Authority:', msalInstance.getConfiguration().auth.authority);
      
      // Use redirect or popup based on interactionMode
      if (options.interactionMode === "redirect") {
        await msalInstance.loginRedirect(loginRequest);
        // loginRedirect doesn't return, it redirects the page
        // This line will never execute, but TypeScript needs a return
        throw new Error("Redirect initiated");
      } else {
        return await msalInstance.loginPopup(loginRequest);
      }
    }
    throw error;
  }
}
// Add this helper function to check if token is expired
function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split(".")[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    const exp = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    
    return now >= (exp - bufferTime);
  } catch (error) {
    return true; // Assume expired if we can't decode
  }
}

// Add this helper to refresh Salesforce token
async function refreshSalesforceToken(apiService?: any): Promise<string | null> {
  try {
    const storedToken = localStorage.getItem("app_jwt");
    if (!storedToken) return null;
    
    // Decode the current token to get user info
    const payload = storedToken.split(".")[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    
    const salesforceUserId = decoded.salesforce_user_id;
    const email = decoded.email;
    
    if (!salesforceUserId || !email) {
      return null;
    }
    
    // Use ApiService if available, otherwise fallback to direct fetch
    let response: Response;
    let data: any;
    
    if (apiService?.salesforceLogin) {
      try {
        data = await apiService.salesforceLogin({ 
          userId: salesforceUserId, 
          email: email 
        });
        const newToken = data.token;
        
        // Store the new token
        localStorage.setItem("app_jwt", newToken);
        
        return newToken;
      } catch (error) {
        // Don't fallback for now, just return null
        return null;
      }
    } else {
      // Fallback to direct fetch (for backwards compatibility)
      response = await fetch('http://localhost:5229/api/auth/salesforce-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: salesforceUserId, 
          email: email 
        })
      });
      
      if (response.ok) {
        data = await response.json();
        const newToken = data.token;
        
        // Store the new token
        localStorage.setItem("app_jwt", newToken);
        
        return newToken;
      } else {
        return null;
      }
    }
  } catch (error) {
    return null;
  }
}

export async function getUnifiedAccessToken(options: LoginOptions, httpServiceToken?: string, apiService?: any): Promise<string> {
  // Priority order:
  // 1. HttpService token (set by AccessProvider via setToken) - check if expired
  // 2. Salesforce JWT in localStorage - check if expired and refresh if needed
  // 3. MSAL fallback

  // Check HttpService token first (this comes from AccessProvider)
  if (httpServiceToken) {
    if (!isTokenExpired(httpServiceToken)) {
      return httpServiceToken;
    }
  }

  // Check for Salesforce JWT in localStorage
  let appJwt = localStorage.getItem("app_jwt");
  if (appJwt) {
    // Check if the token is expired
    if (!isTokenExpired(appJwt)) {
      return appJwt;
    } else {
      // Try to refresh the token
      const refreshedToken = await refreshSalesforceToken(apiService);
      if (refreshedToken) {
        return refreshedToken;
      } else {
        localStorage.removeItem("app_jwt");
      }
    }
  }

  // Fallback to MSAL
  const msalResult = await acquireTokenWithFallback(options);
  return msalResult.accessToken;
}
export async function getUserPhoto(accessToken: string | null): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) return null;
  try {
    let idToken: string | null = null;

    if (accessToken) {
      const claims = decodeJwt(accessToken);
      if (claims.picture) return claims.picture;
      if (claims.family_name) return claims.family_name;
    }

    const result = await acquireTokenWithFallback({
      scopes: ["openid", "profile"],
      prompt: "none"
    });

    idToken = result.idToken ?? null;
    if (!idToken) return null;

    const claims = decodeJwt(idToken);
    if (claims.picture) return claims.picture;
    if (claims.family_name) return claims.family_name;

    return null;
  } catch (error: any) {
    if (error instanceof InteractionRequiredAuthError || 
        error.errorCode === "interaction_required") {
      try {
        const result = await msalInstance.acquireTokenPopup({
          scopes: ["openid", "profile"],
          prompt: "consent"
        });
        const idToken = result.idToken ?? null;
        if (!idToken) return null;
        const claims = decodeJwt(idToken);
        if (claims.picture) return claims.picture;
        if (claims.family_name) return claims.family_name;
        return null;
      } catch (popupError: any) {
        return null;
      }
    }
    return null;
  }
}
// Helper to decode JWT
function decodeJwt(token: string): any {
  const payload = token.split(".")[1];
  return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
}

// export async function getAccessToken(
//   scopes: string[],
//   forceRefresh: boolean = false,
//   prompt?: "login" | "none"
// ): Promise<string> {
//   const request: any = {
//     scopes: scopes,
//     forceRefresh,
//   };
//   if (prompt) request.prompt = prompt;

//   try {
//     const accounts = msalInstance.getAllAccounts();
//     if (accounts.length === 0) throw new Error("No accounts found");

//     const response = await msalInstance.acquireTokenSilent({
//       ...request,
//       account: accounts[0],
//     });

//     return response.accessToken;
//   } catch (error) {
//     if (error instanceof InteractionRequiredAuthError) {
//       const response = await msalInstance.acquireTokenPopup({
//         ...request,
//         account: msalInstance.getAllAccounts()[0],
//       });
//       return response.accessToken;
//     }
//     throw error;
//   }
// }
// // Helper to decode JWT
// function decodeJwt(token: string): any {
//   const payload = token.split(".")[1];
//   return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
// }

// async function getGoogleProfilePhoto(accessToken: string): Promise<string | null> {
//   const response = await fetch(
//     "https://people.googleapis.com/v1/people/me?personFields=photos",
//     {
//       headers: { Authorization: `Bearer ${accessToken}` },
//     }
//   );
//   if (!response.ok) return null;
//   const data = await response.json();
//   if (data.photos && data.photos.length > 0) {
//     return data.photos[0].url;
//   }
//   return null;
// }
// export async function getUserPhoto(accessToken: string|null): Promise<string | null> {
//   const accounts = msalInstance.getAllAccounts();
//   if (!accounts.length) return null;
//   try {
//     console.log("Getting user photo ", accessToken);
//     // Get ID token (not access token)
//     const response = accessToken ?? await getAccessToken(
//       ["openid", "profile"]);
//     // const idToken = response.idToken;
//     // if (!idToken) return null;
//     const claims = decodeJwt(response);
//     // 1. Try claims.picture
//     if (claims.family_name) return claims.family_name;

//     // // 2. If idp is google.com, try Google People API
//     // if (claims.idp === "google.com") {
//     //   // Try to get a Google access token (must be configured in Entra External ID)
//     //   try {
//     //     const googleTokenResponse = await msalInstance.acquireTokenSilent({
//     //       scopes: ["https://www.googleapis.com/auth/userinfo.profile"],
//     //       account: accounts[0],

